/* eslint-disable @typescript-eslint/ban-ts-comment */
import { TableTemplate } from '@repo/types';
import {
  ColumnDef,
  Row,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table';
import {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  ReactNode,
  useMemo,
} from 'react';
import { AppTableProvider } from './lib/context/use-app-table-context';
import { AppTableRef } from './types/table-props';
import { DefaultTable } from './ui/default-table';
import { VirtualTable } from './ui/virtual-table';

export interface AppTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  className?: string;
  rowClassName?: string;
  rowHeight?: number;
  calculatedRowClassName?: (row: TData, additionalProps?: Row<TData>) => string;
  calculatedRowStyle?: (
    row: TData,
    additionalProps?: Row<TData>,
  ) => CSSProperties;
  presentationType?: 'default' | 'virtual';
  isLoading?: boolean;
  isResizable?: boolean;
  isDraggable?: boolean;
  rowSelection?: RowSelectionState;
  hiddenColumns?: string[];
  disablePagination?: boolean;
  disableToolbar?: boolean;
  enableColumnFilter?: boolean;
  enableFooter?: boolean;
  absoluteColumnWidth?: boolean;
  columnPinning?: {
    left?: string[];
    right?: string[];
  };
  customSelectColumn?: boolean;
  //Templates
  isTemplate?: boolean;
  templates?: TableTemplate[];
  selectedTemplateId?: number | null;
  onSelectTemplate?: (template: TableTemplate) => void;
  onDeleteTemplate?: (template: TableTemplate) => void;
  //
  setRowSelection?: (rowSelection: RowSelectionState) => void;
  defaultSorting?: SortingState;
  onAddNew?: () => void;
  onImport?: (file: File) => void;
  onExcel?: (columns: string[], rows: Partial<TData>[]) => void;
  onPdf?: (columns: string[], rows: Partial<TData>[]) => void;
  onDelete?: () => void;
  onDoubleClickRow?: (row: TData) => void;
  onClickRow?: (row: TData) => void;
  getRowId?: (row: TData) => string;
  CustomToolbar?: ReactNode;
  CustomFilter?: ReactNode;
}

const AppTableComponent = <TData,>(
  {
    presentationType = 'default',
    isResizable = true,
    disablePagination = false,
    disableToolbar = false,
    ...props
  }: AppTableProps<TData>,
  ref: ForwardedRef<AppTableRef<TData>>,
) => {
  const TableComponent = useMemo(() => {
    if (presentationType === 'default') return DefaultTable;
    return VirtualTable;
  }, [presentationType]);

  return (
    <AppTableProvider<TData>
      disablePagination={disablePagination}
      disableToolbar={disableToolbar}
      isResizable={isResizable}
      {...props}
    >
      {/* @ts-expect-error */}
      <TableComponent ref={ref} />
    </AppTableProvider>
  );
};

export const AppTable = forwardRef(AppTableComponent) as <TData>(
  props: AppTableProps<TData> & {
    ref?: ForwardedRef<AppTableRef<TData>>;
  },
) => ReturnType<typeof AppTableComponent>;
